import { useState } from "react";
import { X, Mic, Volume2, Play } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AUDIO_EXTS = ["ogg", "mp3", "m4a", "wav"];
const VIDEO_EXTS = ["mp4", "mov", "m4v", "webm"];

const extOf = (url) => (url || "").split("?")[0].split("#")[0].split(".").pop().toLowerCase();
const isAudioUrl = (url) => AUDIO_EXTS.includes(extOf(url));
const isVideoUrl = (url) => VIDEO_EXTS.includes(extOf(url));

// Matchar backendens _safe_url_key (urlsafe base64 utan padding)
const safeUrlKey = (url) => {
  try {
    const b = btoa(unescape(encodeURIComponent(url)));
    return b.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  } catch {
    return url;
  }
};

/**
 * Publik visning av galleri-poster (bilder, videor, ljud).
 * Props:
 *   bilder: string[] – URL-lista
 *   galleriAudio: { [safeKey]: audio_url } – valfri röstbeskrivning per bild/video
 *   alt: string
 */
export default function GalleriStrip({ bilder = [], galleriAudio = {}, alt = "Galleri" }) {
  const [lightboxIdx, setLightboxIdx] = useState(null);
  if (!bilder || bilder.length === 0) return null;
  const src = (u) => (u && u.startsWith("/")) ? `${BACKEND_URL}${u}` : u;

  const current = lightboxIdx != null ? bilder[lightboxIdx] : null;
  const currentAudio = current ? (galleriAudio?.[safeUrlKey(current)] || null) : null;

  return (
    <>
      <div data-testid="galleri-strip" className="mt-3 overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0 pb-2">
        <div className="flex gap-2 min-w-min">
          {bilder.map((url, i) => {
            const audio = galleriAudio?.[safeUrlKey(url)] || null;
            const isAudio = isAudioUrl(url);
            const isVideo = isVideoUrl(url);
            return (
              <button
                key={url + i}
                onClick={() => setLightboxIdx(i)}
                data-testid={`galleri-thumb-${i}`}
                aria-label={isAudio ? `Ljudklipp ${i + 1}` : `${alt} ${i + 1}${audio ? " med röstbeskrivning" : ""}`}
                className="relative w-24 h-24 sm:w-28 sm:h-28 shrink-0 rounded-sm overflow-hidden border border-warm-border hover:border-falu/60 transition-colors bg-ink group"
              >
                {isAudio ? (
                  <div className="w-full h-full flex flex-col items-center justify-center bg-pine/20 text-parchment gap-1">
                    <Volume2 size={22} />
                    <span className="text-[9px] font-body uppercase tracking-wider">Ljud</span>
                  </div>
                ) : isVideo ? (
                  <>
                    <video src={src(url) + "#t=0.5"} muted playsInline preload="metadata" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 flex items-center justify-center bg-ink/25 group-hover:bg-ink/10 transition-colors">
                      <Play size={22} className="text-parchment drop-shadow" />
                    </div>
                  </>
                ) : (
                  <img src={src(url)} alt={alt} loading="lazy" className="w-full h-full object-cover" />
                )}
                {audio && !isAudio && (
                  <span
                    className="absolute bottom-1 right-1 bg-pine text-parchment rounded-full p-1 shadow"
                    title="Röstbeskrivning tillgänglig"
                    aria-hidden="true"
                  >
                    <Mic size={10} />
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {lightboxIdx != null && (
        <div
          className="fixed inset-0 z-[100] bg-ink/95 flex items-center justify-center p-4 sm:p-10"
          onClick={() => setLightboxIdx(null)}
        >
          <button
            onClick={() => setLightboxIdx(null)}
            className="absolute top-4 right-4 text-parchment/80 hover:text-parchment p-2"
            aria-label="Stäng"
            data-testid="galleri-lightbox-close"
          >
            <X size={28} />
          </button>

          <div
            className="flex flex-col items-center gap-4 max-w-full max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
          >
            {isAudioUrl(current) ? (
              <div className="bg-parchment/10 border border-parchment/20 rounded-sm p-6 sm:p-10 flex flex-col items-center gap-4">
                <Volume2 size={48} className="text-parchment/80" />
                <audio
                  src={src(current)}
                  controls
                  autoPlay
                  className="w-full max-w-md"
                  data-testid="galleri-lightbox-audio"
                />
              </div>
            ) : isVideoUrl(current) ? (
              <video
                src={src(current)}
                controls
                autoPlay
                className="max-w-full max-h-[80vh] object-contain"
                data-testid="galleri-lightbox-video"
              />
            ) : (
              <img
                src={src(current)}
                alt={alt}
                className="max-w-full max-h-[80vh] object-contain"
                data-testid="galleri-lightbox-img"
              />
            )}

            {currentAudio && !isAudioUrl(current) && (
              <div className="w-full max-w-md bg-parchment/10 border border-parchment/20 rounded-sm p-3 flex items-center gap-2">
                <Mic size={16} className="text-parchment/80 shrink-0" />
                <audio
                  src={src(currentAudio)}
                  controls
                  preload="auto"
                  className="flex-1 min-w-0"
                  data-testid="galleri-lightbox-voice"
                />
              </div>
            )}
          </div>

          {bilder.length > 1 && (
            <div className="absolute bottom-6 left-0 right-0 text-center text-parchment/60 text-xs pointer-events-none">
              {lightboxIdx + 1} / {bilder.length} &middot; ESC för att stänga
            </div>
          )}
        </div>
      )}
    </>
  );
}
